import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Globe, ArrowRight, Clock, DollarSign, ExternalLink, 
  ChevronRight, Check, AlertCircle, Zap, Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVisaPage = () => {
  const navigate = useNavigate();
  const [evisaCountries, setEvisaCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryDetails, setCountryDetails] = useState(null);

  useEffect(() => {
    fetchEvisaCountries();
  }, []);

  const fetchEvisaCountries = async () => {
    try {
      const response = await axios.get(`${API}/evisa`);
      setEvisaCountries(response.data.evisa_countries);
    } catch (error) {
      console.error("Error fetching eVisa countries:", error);
      toast.error("Failed to load eVisa information");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryDetails = async (countryCode) => {
    try {
      const response = await axios.get(`${API}/evisa/${countryCode}`);
      setCountryDetails(response.data);
      setSelectedCountry(countryCode);
    } catch (error) {
      console.error("Error fetching eVisa details:", error);
      toast.error("Failed to load eVisa details");
    }
  };

  const filteredCountries = evisaCountries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-20" data-testid="evisa-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-8">
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Link to="/" className="hover:text-[#0F172A]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0F172A] font-medium">eVisa Services</span>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="flex items-center gap-2 text-[#E15636] mb-4">
          <Zap className="w-5 h-5" />
          <span className="text-sm font-medium uppercase tracking-widest">Electronic Visas</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-heading tracking-tight text-[#0F172A] mb-4">
          eVisa <span className="text-[#E15636]">Services</span>
        </h1>
        <p className="text-lg text-[#64748B] max-w-2xl">
          Apply for electronic visas online without visiting an embassy. 
          Fast, convenient, and available for many destinations worldwide.
        </p>
      </section>

      {/* Search */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <Input
            type="text"
            placeholder="Search countries..."
            className="pl-10 h-12 rounded-full border-slate-200"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-evisa"
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Countries List */}
          <div className="lg:col-span-1">
            <h2 className="text-xl font-heading text-[#0F172A] mb-4">
              Countries with eVisa
            </h2>
            {loading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton h-16 rounded-xl"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="evisa-list">
                {filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => fetchCountryDetails(country.code)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      selectedCountry === country.code
                        ? 'border-[#E15636] bg-[#E15636]/5'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                    data-testid={`evisa-country-${country.code}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{country.flag}</span>
                      <div className="flex-1">
                        <h3 className="font-medium text-[#0F172A]">{country.name}</h3>
                        <p className="text-xs text-[#64748B]">
                          {country.has_evisa ? country.evisa_name : "No eVisa"}
                        </p>
                      </div>
                      {country.has_evisa ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          CAD ${country.fee_cad}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">N/A</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Country Details */}
          <div className="lg:col-span-2">
            {countryDetails ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="evisa-details">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">{countryDetails.flag_emoji}</span>
                    <div>
                      <h2 className="text-2xl font-heading text-[#0F172A]">
                        {countryDetails.country_name}
                      </h2>
                      <p className="text-[#E15636] font-medium">{countryDetails.evisa_name}</p>
                    </div>
                  </div>
                  {countryDetails.has_evisa && (
                    <a 
                      href={countryDetails.official_portal}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="rounded-full bg-[#E15636] hover:bg-[#C24125]">
                        Apply Now
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  )}
                </div>

                {/* Description */}
                <p className="text-[#64748B] mb-6">{countryDetails.evisa_description}</p>

                {/* Quick Stats */}
                {countryDetails.has_evisa && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                        <DollarSign className="w-4 h-4" />
                        Fee
                      </div>
                      <p className="text-xl font-heading text-[#0F172A]">CAD ${countryDetails.fee_cad}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                        <Clock className="w-4 h-4" />
                        Processing
                      </div>
                      <p className="text-lg font-heading text-[#0F172A]">{countryDetails.processing_time}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 col-span-2">
                      <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                        <Globe className="w-4 h-4" />
                        Validity
                      </div>
                      <p className="text-lg font-heading text-[#0F172A]">{countryDetails.validity}</p>
                    </div>
                  </div>
                )}

                {/* Eligible Countries */}
                {countryDetails.eligible_countries && (
                  <div className="mb-6">
                    <h3 className="font-heading text-lg text-[#0F172A] mb-2">Eligible Countries</h3>
                    <p className="text-[#64748B]">{countryDetails.eligible_countries}</p>
                  </div>
                )}

                {/* Requirements */}
                {countryDetails.requirements?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-heading text-lg text-[#0F172A] mb-4">Requirements</h3>
                    <div className="space-y-3">
                      {countryDetails.requirements.map((req, index) => (
                        <div 
                          key={index}
                          className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                        >
                          <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-[#0F172A]">{req.name}</h4>
                            <p className="text-sm text-[#64748B]">{req.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {countryDetails.notes?.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-heading text-lg text-[#0F172A]">Important Notes</h3>
                    {countryDetails.notes.map((note, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
                      >
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">{note}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Official Portal Link */}
                {countryDetails.has_evisa && (
                  <div className="mt-8 p-6 bg-[#0F172A] rounded-xl text-center">
                    <h3 className="font-heading text-lg text-white mb-2">Ready to Apply?</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Apply through the official government portal only
                    </p>
                    <a 
                      href={countryDetails.official_portal}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="rounded-full bg-[#E15636] hover:bg-[#C24125]">
                        Visit Official Portal
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <Globe className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
                <h3 className="text-xl font-heading text-[#0F172A] mb-2">Select a Country</h3>
                <p className="text-[#64748B]">
                  Choose a country from the list to view eVisa details and requirements
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mt-12">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading text-lg text-amber-800 mb-2">Educational Purpose Only</h3>
              <p className="text-amber-700 text-sm">
                This information is for guidance only. Always apply through official government portals. 
                Fees and requirements are subject to change. Verify current information before applying.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EVisaPage;
